import React from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface ConfirmationModalProps {
  isOpen: boolean; onClose: () => void; title: string; message: string; type?: 'success' | 'warning' | 'error' | 'info';
  confirmText?: string; cancelText?: string; showCancel?: boolean; onConfirm?: () => void; icon?: React.ReactNode;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen, onClose, title, message, type = 'info', confirmText = 'Aceptar',
  cancelText = 'Cancelar', showCancel = false, onConfirm, icon
}) => {
  if (!isOpen) return null;
  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          titleColor: 'text-custom-blue',
          iconColor: 'text-custom-blue',
          buttonColor: 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
        };
      case 'warning':
        return {
          titleColor: 'text-yellow-800',
          iconColor: 'text-yellow-500',
          buttonColor: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
        };
      case 'error':
        return {
          titleColor: 'text-red-800',
          iconColor: 'text-red-500',
          buttonColor: 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
        };
      default:
        return {
          titleColor: 'text-blue-800',
          iconColor: 'text-blue-500',
          buttonColor: 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
        };
    }
  };

  const getDefaultIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="mx-auto h-16 w-16 text-green-600" />;
      case 'warning':
        return <ExclamationTriangleIcon className="mx-auto h-16 w-16 text-yellow-500" />;
      case 'error':
        return <ExclamationCircleIcon className="mx-auto h-16 w-16 text-red-500" />;
      default:
        return <InformationCircleIcon className="mx-auto h-16 w-16 text-blue-500" />;
    }
  };

  const styles = getTypeStyles();
  const defaultIcon = getDefaultIcon();

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div className="relative bg-white rounded-lg shadow-xl p-8 m-4 max-w-md w-full">
        <div className="flex justify-between items-center mb-6">
          <h2 className={`text-2xl font-bold ${styles.titleColor}`}>{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="text-center py-6">
          {icon || defaultIcon}
          <p className="mt-2 text-sm text-gray-500">
            {message}
          </p>
        </div>

        <div className="flex justify-end space-x-4 mt-6">
          {showCancel && (
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={handleConfirm}
            className={`px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${styles.buttonColor}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal; 